import React, { useEffect, useState } from "react";
import styled from "styled-components";
import BackPage from "../components/BackPage";
import params from "../utils/params";
import { stripObjectPlaceholders } from "../utils/stripObjectPlaceholders";

// 96 is used as Puppeteer is having a weird 0.75 scale issue. So 72dpi / 0.75 = 96
const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  @media print {
    page-break-after: always;
  }
  height: ${(props) => ((props.pageHeight + 46) / 25.4) * 96}px;
  width: ${(props) => ((props.pageWidth + props.spineWidth + 46) / 25.4) * 96}px;

  ${(props) =>
    props.view === "print" &&
    `
    @page {
      size: ${((props.pageWidth + props.spineWidth + 46) / 25.4) * 96}px ${
      ((props.pageHeight + 46) / 25.4) * 96
    }px;
    }
    * {
      -webkit-print-color-adjust: exact;
    }
  `}
`;
const Spine = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  ${(props) =>
    props.width &&
    `
    width: ${(props.width / 25.4) * 96}px;
  `}
  ${(props) =>
    props.height &&
    `
    height:${(props.height / 25.4) * 96}px;
  `}
`;

const SpineText = styled.div`
  transform: rotate(90deg);
  font-family: "Quicksand", "Noto Color Emoji", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  font-weight: 100;
  text-transform: uppercase;
  font-size: 1.75vh;
  ${(props) =>
    props.width &&
    `
    line-height: ${(props.width / 25.4) * 96}px;
  `}
  white-space: nowrap;
  text-align: center;
`;

const BackPageContainer = styled.div`
  height: ${(props) => (props.pageHeight / 25.4) * 96}px;
  width: ${(props) => (props.pageWidth / 25.4) * 96}px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Cover = () => {
  const { view, user: userId, book: bookId, identity } = params;

  const [frontPage, setFrontPage] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [spineText, setSpineText] = useState("");

  // ImageWrap Books spine width specifications
  // Point conversion: pt * 25.4 / 72 = mm
  const getSpineWidth = (pageCount) => {
    if (pageCount >= 20 && pageCount <= 60) {
      return 6.7564; // 19.152 pt = 0.266 in
    }
    if (pageCount >= 61 && pageCount <= 128) {
      return 11.2522; // 31.896 pt = 0.443 in
    }
    if (pageCount >= 129 && pageCount <= 200) {
      return 15.748; // 44.64 pt = 0.62 in
    }
    if (pageCount >= 201 && pageCount <= 272) {
      return 20.2438; // 57.384 pt = 0.797 in
    }
    if (pageCount >= 273 && pageCount <= 340) {
      return 24.7396; // 70.128 pt = 0.974 in
    }
    if (pageCount >= 341 && pageCount <= 380) {
      return 29.2354; // 82.872 pt = 1.151 in
    }
    return 6.7564; // Default to minimum spine width
  };

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const getBook = await fetch(
          `${process.env.REACT_APP_PUBLIC_API_ENDPOINT}/user/${userId}/book/${bookId}`,
          {}
        );
        const json = await getBook.json();
        setPageCount(json?.customPages?.items?.filter((page) => !page.hide).length - 1);
        const _frontPage = json?.customPages?.items?.find((item) => item.number === 0);
        setFrontPage(_frontPage);
        const _spineText =
          _frontPage?.customQuestions?.items?.find((item) => item.position === 0)?.answers
            ?.items?.[0]?.text ||
          _frontPage?.customQuestions?.items?.find((item) => item.position === 0)?.placeholder;
        setSpineText(stripObjectPlaceholders(_spineText));
      } catch (e) {
        // alert(e);
        console.log(e.message);
      }
    };

    if (view === "print") {
      fetchBook();
    }
  }, [bookId, userId, view]);

  const pageWidth = 612 / 2;
  const pageHeight = 305;
  const spineWidth = getSpineWidth(pageCount);
  return (
    <Container
      view={view}
      pageHeight={pageHeight}
      pageWidth={pageWidth * 2}
      spineWidth={spineWidth}
    >
      <>
        <BackPageContainer pageHeight={pageHeight} pageWidth={pageWidth} spineWidth={spineWidth}>
          <BackPage width={pageWidth} height={pageHeight} />
        </BackPageContainer>
        {frontPage && (
          <>
            <Spine text={frontPage} width={getSpineWidth(pageCount)} height={pageHeight}>
              <SpineText width={getSpineWidth(pageCount)}>{spineText}</SpineText>
            </Spine>
            <iframe
              src={`${window.location.origin}/page?user=${userId}&book=${bookId}&page=${frontPage?.id}&identity=${identity}&view=${view}`}
              height={`${(pageHeight / 25.4) * 96}px`}
              width={`${(pageWidth / 25.4) * 96}px`}
              title="Front Page"
            ></iframe>
          </>
        )}
      </>
    </Container>
  );
};

export default Cover;
